// ═══════════════════════════════════════════════════════════════════
// JustArrivedShowcase (Apr28 v4)
// ═══════════════════════════════════════════════════════════════════
// Premium "Just Arrived" section — gets more visual attention than
// regular category rails. Sits above the per-category rails on the
// homepage to create a "this week's drop" moment.
//
// Visual treatment:
//   - Larger title with gold gradient
//   - Sparkle decorations + "NEW" pulse badge
//   - Bigger card sizes with more breathing room
//   - Stronger hover effects (lift + glow)
//   - "✨ Fresh Drop" eyebrow tag
//   - Animated "NEW" ribbon corner on each card
//
// Behaviour:
//   - Same auto-scroll mechanic as CategoryRail
//   - Pulls the 12 most recent products (sorted by createdAt DESC)
//   - Hidden if products.length === 0 (parent handles)
// ═══════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PremiumProductCard from '../shop/PremiumProductCard';

const AUTO_SCROLL_MS = 4500;
const SCROLL_PX = 320;

const JustArrivedShowcase = ({ products, hi, isMobile, isSmall }) => {
  const scrollRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.15 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (paused || !inView) return;
    const id = setInterval(() => {
      const el = scrollRef.current;
      if (!el) return;
      const atEnd = el.scrollLeft + el.clientWidth + 10 >= el.scrollWidth;
      if (atEnd) el.scrollTo({ left: 0, behavior: 'smooth' });
      else el.scrollBy({ left: SCROLL_PX, behavior: 'smooth' });
    }, AUTO_SCROLL_MS);
    return () => clearInterval(id);
  }, [paused, inView]);

  if (!products || products.length === 0) return null;

  return (
    <section style={S.section}>
      {/* Decorative sparkles */}
      <div style={{ ...S.sparkle, top: '8%', left: '6%' }}>✦</div>
      <div style={{ ...S.sparkle, top: '15%', right: '8%', fontSize: 14 }}>✧</div>
      <div style={{ ...S.sparkle, bottom: '20%', left: '12%', fontSize: 18 }}>✦</div>

      {/* Header */}
      <div style={{ ...S.header, padding: isMobile ? '0 14px 22px' : '0 32px 26px' }}>
        <div>
          <div style={S.eyebrow}>
            <span style={S.pulseDot}/>
            {hi ? '✨ ताज़ा कलेक्शन' : '✨ Fresh Drop'}
          </div>
          <h2 style={{ ...S.title, fontSize: isMobile ? 26 : 36 }}>
            {hi ? 'अभी ' : 'Just '}
            <em style={S.titleEm}>{hi ? 'आया' : 'Arrived'}</em>
          </h2>
          <p style={S.subtitle}>
            {hi
              ? 'इस हफ़्ते के सबसे नए चयन — सीधे ज्योति के संग्रह से'
              : "This week's freshest picks — handpicked by Jyoti"}
          </p>
        </div>
        <Link to="/shop?sort=newest" style={S.viewAll}>
          {hi ? 'सब देखें' : 'Shop All New'}
          <span style={S.arrow}>→</span>
        </Link>
      </div>

      {/* Premium decorated divider */}
      <div style={S.divider}>
        <span style={S.dividerLine} />
        <span style={S.dividerDiamond}>◆</span>
        <span style={S.dividerLine} />
      </div>

      {/* Cards rail */}
      <div
        ref={scrollRef}
        style={{
          ...S.rail,
          padding: isMobile ? '14px 14px 24px' : '14px 32px 28px',
        }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setTimeout(() => setPaused(false), 2000)}
      >
        {products.slice(0, 12).map(p => {
          const cardWidth = isSmall ? 180 : isMobile ? 200 : 270;
          return (
            <div key={p._id} style={{ flex: '0 0 auto', width: cardWidth, scrollSnapAlign: 'start' }}>
              <PremiumProductCard product={p} hi={hi} variant="showcase" />
            </div>
          );
        })}
      </div>

      {/* Pulse animation for the NEW dot */}
      <style>{`
        @keyframes ggPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.4); opacity: 0.6; }
        }
        @keyframes ggSparkle {
          0%, 100% { opacity: 0.25; transform: translateY(0); }
          50%      { opacity: 0.6;  transform: translateY(-4px); }
        }
      `}</style>
    </section>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────
const S = {
  section: {
    position: 'relative',
    padding: '50px 0 30px',
    background: `
      radial-gradient(ellipse at top, rgba(201,168,76,0.06), transparent 60%),
      transparent
    `,
    overflow: 'hidden',
  },

  sparkle: {
    position: 'absolute',
    color: 'rgba(201,168,76,0.3)',
    fontSize: 16,
    pointerEvents: 'none',
    animation: 'ggSparkle 4s ease-in-out infinite',
  },

  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 20,
    flexWrap: 'wrap',
  },
  eyebrow: {
    color: '#c9a84c',
    fontSize: 12,
    letterSpacing: 3,
    fontWeight: 700,
    fontFamily: 'system-ui',
    marginBottom: 12,
    textTransform: 'uppercase',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  pulseDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#22c55e',
    boxShadow: '0 0 0 3px rgba(34,197,94,0.25)',
    animation: 'ggPulse 1.6s ease-in-out infinite',
  },
  title: {
    margin: 0,
    color: '#e8e8f5',
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: '-0.5px',
  },
  titleEm: {
    fontStyle: 'italic',
    background: 'linear-gradient(135deg, #c9a84c 0%, #f0d080 50%, #c9a84c 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  subtitle: {
    color: 'rgba(232,232,245,0.55)',
    fontSize: 13,
    fontFamily: 'system-ui',
    fontStyle: 'italic',
    margin: '10px 0 0',
  },
  viewAll: {
    background: 'linear-gradient(135deg, #c9a84c, #f0d080)',
    color: '#0a0810',
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: 1,
    textDecoration: 'none',
    fontFamily: 'system-ui',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '11px 22px',
    borderRadius: 24,
    boxShadow: '0 6px 20px rgba(201,168,76,0.3)',
    whiteSpace: 'nowrap',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  arrow: { fontSize: 15 },

  divider: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    padding: '0 32px',
    marginBottom: 4,
  },
  dividerLine: {
    flex: 1,
    maxWidth: 100,
    height: 1,
    background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.4), transparent)',
  },
  dividerDiamond: { color: '#c9a84c', fontSize: 12 },

  rail: {
    display: 'flex',
    gap: 18,
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollSnapType: 'x proximity',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(201,168,76,0.3) transparent',
  },

  // Card chrome is now handled by PremiumProductCard. The S object
  // above is only the showcase scaffolding (section, header, sparkles,
  // etc). Card-internal styles intentionally removed in v6.
};

export default JustArrivedShowcase;
